<template>
  <!-- Logged-out materials bank ("vitrina") for the Pruebas Informales demo.
       Download works without login; correction and class material invite login. -->
  <div class="mbank" :class="{ 'mbank--embedded': embedded }">
    <InformalTestsVitrina
      guest-mode
      :embedded="embedded"
      :active-mode="activeMode"
      :grades="grades"
      :subjects="subjects"
      :materials="materials"
      :selected-grade="selectedGrade"
      :selected-subject="selectedSubject"
      :selected-material="selectedMaterial"
      :loading-grades="loadingGrades"
      :loading-subjects="loadingSubjects"
      :loading-materials="loadingMaterials"
      @mode-change="activeMode = $event"
      @grade-change="onGradeChange"
      @subject-change="onSubjectChange"
      @material-change="onMaterialChange"
      @download="download"
      @login="login"
    >
      <template #correct>
        <MaterialScoreInput
          v-if="selectedMaterial"
          :key="scoreInputKey"
          :context="context"
          :can-work="false"
        />
        <p v-else class="mbank-muted">Selecciona una prueba para ver el formulario.</p>
      </template>
      <template #class-material>
        <MaterialAiGenerate
          :context="context"
          :can-work="false"
          embedded-in-vitrina
        />
      </template>
    </InformalTestsVitrina>
  </div>
</template>

<script>
// Plain axios (no Authorization header) on purpose: the demo is only shown to
// anonymous visitors, and the shared `api` instance would send `Token null`,
// which the backend rejects with 401. get_filtered_materials is read-only
// (IsAuthenticatedOrReadOnly); with an empty school it returns only the shared
// material_school catalog (the non-custom materials).
import axios from "axios";
import InformalTestsVitrina from "./InformalTestsVitrina.vue";
import MaterialScoreInput from "./MaterialScoreInput.vue";
import MaterialAiGenerate from "./MaterialAiGenerate.vue";
import { medirPublico } from "@/services/analytics";
import {
  DEFAULT_GRADE,
  DEFAULT_SUBJECT,
  SPECIAL_SUBJECTS,
  pickDefaultMaterial,
} from "./informalTestsDefaults";

export default {
  name: "MaterialsBankDemo",
  components: {
    InformalTestsVitrina,
    MaterialScoreInput,
    MaterialAiGenerate,
  },
  // Absorb the props ChatMessageComponent binds on the custom component.
  props: {
    condition: { type: String, required: false, default: "" },
    context: { type: Object, required: false, default: () => ({}) },
    canWork: { type: Boolean, required: false, default: false },
    embedded: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      activeMode: "download",
      grades: [],
      loadingGrades: false,
      selectedGrade: null,
      subjects: [],
      loadingSubjects: false,
      selectedSubject: null,
      materials: [],
      loadingMaterials: false,
      selectedMaterial: null,
      scoreInputKey: 0,
    };
  },
  mounted() {
    this.fetchGrades().then(() => {
      this.selectedGrade =
        this.grades.find((g) => DEFAULT_GRADE.test(g.original_name)) || null;
      if (!this.selectedGrade) return null;
      return this.fetchSubjects().then(() => {
        this.selectedSubject =
          this.subjects.find((s) => DEFAULT_SUBJECT.test(s.name)) || null;
        if (this.selectedSubject) return this.fetchMaterials();
      });
    });
  },
  methods: {
    fetchGrades() {
      this.loadingGrades = true;
      this.grades = [];
      return axios
        .get("/api/get_grades_list/")
        .then((res) => {
          this.grades = ((res.data && res.data.grades) || [])
            .filter((g) => ![14, 15, 16].includes(g[0]))
            .map((g) => ({ code: g[0], original_name: g[1] }));
          this.loadingGrades = false;
        })
        .catch(() => {
          this.grades = [];
          this.loadingGrades = false;
        });
    },
    fetchSubjects() {
      this.loadingSubjects = true;
      this.subjects = [];
      return axios
        .get("/api/test-tags/get_subject_hashtags/", {
          params: { grade: this.selectedGrade.original_name },
        })
        .then((res) => {
          this.subjects = (res.data || []).filter((s) =>
            SPECIAL_SUBJECTS.includes(s.name)
          );
          this.loadingSubjects = false;
        })
        .catch(() => {
          this.subjects = [];
          this.loadingSubjects = false;
        });
    },
    fetchMaterials() {
      this.loadingMaterials = true;
      this.materials = [];
      this.selectedMaterial = null;
      return axios
        .get("/api/custom-materials/get_filtered_materials/", {
          params: {
            school: "",
            subject: this.selectedSubject.enum_code,
            grade: this.selectedGrade.code,
          },
        })
        .then((res) => {
          this.materials = (res.data && res.data.materials) || [];
          this.selectedMaterial = pickDefaultMaterial(
            this.materials,
            this.selectedSubject.name
          );
          this.syncCorrectionStore();
          this.loadingMaterials = false;
        })
        .catch(() => {
          this.materials = [];
          this.loadingMaterials = false;
        });
    },
    syncCorrectionStore() {
      if (!this.selectedMaterial || !this.selectedGrade) return;
      const commit = (name, key, value) =>
        this.$store.commit("chatbot/updateMessage", { name, key, value });
      commit(
        "existing_materials_list",
        "selectedMaterialId",
        this.selectedMaterial.id
      );
      commit(
        "existing_materials_list",
        "selectedMaterialName",
        this.selectedMaterial.name
      );
      commit(
        "existing_materials_list",
        "selectedMaterialPdf",
        this.selectedMaterial.pdf || null
      );
      commit("grade", "answer", {
        code: this.selectedGrade.code,
        original_name: this.selectedGrade.original_name,
      });
      this.scoreInputKey += 1;
    },
    onGradeChange(grade) {
      this.selectedGrade = grade;
      this.selectedSubject = null;
      this.subjects = [];
      this.materials = [];
      this.selectedMaterial = null;
      if (this.selectedGrade) this.fetchSubjects();
    },
    onSubjectChange(subject) {
      this.selectedSubject = subject;
      this.materials = [];
      this.selectedMaterial = null;
      if (this.selectedSubject) this.fetchMaterials();
    },
    onMaterialChange(material) {
      if (!material || !material.pdf) return;
      this.selectedMaterial = material;
      this.syncCorrectionStore();
    },
    download(material) {
      if (!material || !material.pdf) {
        this.$toasted.error("Este material no tiene PDF disponible.");
        return;
      }
      // La vitrina no es una demostracion, es un catalogo: no tiene
      // principio ni final que contar. Lo que si es una conversion es
      // esto, y se puede sin sesion — el catalogo se pide con axios
      // pelado justamente porque el visitante es anonimo.
      medirPublico("descarga_muestra", material.name, "Pruebas Informales");
      window.open(material.pdf, "_blank", "noopener");
    },
    login() {
      this.$emit("go-to-step", "login");
    },
  },
};
</script>

<style scoped>
.mbank {
  --c: var(--main-color, #e84855);
  --c-soft: var(--secondary-color, #fbe1e4);
  font-family: "Ubuntu", Arial, sans-serif;
  border: 2px solid var(--c);
  border-top-width: 5px;
  border-radius: 16px;
  background: #fff;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
@media (min-width: 768px) {
  .mbank {
    height: 70vh;
    overflow-y: auto;
  }
}
@media (max-width: 575.98px) {
  .mbank {
    padding: 14px;
    border-radius: 14px;
  }
}
.mbank--embedded {
  border: 0;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
  height: auto;
  overflow: visible;
}
.mbank-muted {
  font-size: 0.84rem;
  color: #9a8f91;
  margin: 8px 0;
}
</style>
